import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import {
  uploadVideoWithOptions,
  uploadSubtitleWithOptions,
  processVideoFromUrlWithOptions,
  getAllTasks,
  cancelTask,
  retryTask,
  deleteTask,
  getLanguages,
  getPromptPresets,
  getAdminStats,
  getLimits,
} from './api';
import { UI_LANGUAGE_STORAGE_KEY, mapBrowserLanguageToSupported } from './i18n';
import EditorPage from './EditorPage';
import GlossaryEditorPage from './GlossaryEditorPage';
import Tooltip from './Tooltip';

const FALLBACK_LANGUAGE_OPTIONS = [
  { code: 'zh-Hans', name: 'Simplified Chinese' },
  { code: 'zh-Hant', name: 'Traditional Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'en', name: 'English' },
];

const ACCESS_CODE_STORAGE_KEY = 'gongju_access_code';

function normalizeAccessCode(raw) {
  const s = String(raw || '').trim();
  // Common operator error: copying "<ACCESS_CODE>" with angle brackets.
  if (s.startsWith('<') && s.endsWith('>') && s.length > 2) return s.slice(1, -1).trim();
  return s;
}

const VIDEO_EXTS = [
  '.mp4',
  '.mkv',
  '.mov',
  '.avi',
  '.webm',
  '.flv',
  '.m4v',
  '.ts',
  '.mpeg',
  '.mpg',
];

// Cost estimate inputs (update when vendor pricing changes).
const DEEPSEEK_USD_PER_1M_TOKENS = 0.2;
const DEEPGRAM_USD_PER_AUDIO_MINUTE = 0.0043;

const GLOSSARY_SAMPLE_YAML = `# Example glossary (YAML)
#
# Notes:
# - mode=strict: MUST use the specified translation.
# - mode=prefer: Prefer the translation; if variants exist, choose based on context.
version: 1
meta:
  name: "my-glossary"
entries:
  - source: "MrBeast"
    term_type: person
    mode: strict
    aliases: ["Mr Beast"]
    note: "YouTuber name"
    targets:
      zh-Hans:
        text: "野兽先生"
      zh-Hant:
        text: "野獸先生"
      en:
        text: "MrBeast"

  - source: "bank"
    term_type: tech
    mode: prefer
    note: "Multi-meaning example"
    targets:
      zh-Hans:
        variants:
          - text: "银行"
            note: "Finance context"
            confidence: 0.6
          - text: "河岸"
            note: "Geography context"
            confidence: 0.4
        default: "银行"
`;

function formatStatusLabel(status, t) {
  const raw = String(status || '').trim();
  if (!raw) return '';
  return t(`status.${raw}`, { defaultValue: raw });
}

function formatTaskOptionsLabel(options, t) {
  const opts = options && typeof options === 'object' ? options : null;
  if (!opts) return '';

  const sep = t('taskOptions.sep');
  const parts = [];

  if (Array.isArray(opts.target_languages) && opts.target_languages.length > 0) {
    parts.push(t('taskOptions.targets', { value: opts.target_languages.join(', ') }));
  }
  if (typeof opts.ass_enabled === 'boolean') {
    parts.push(t('taskOptions.ass', { value: opts.ass_enabled ? t('taskOptions.on') : t('taskOptions.off') }));
  }
  if (typeof opts.generate_title === 'boolean') {
    parts.push(t('taskOptions.title', { value: opts.generate_title ? t('taskOptions.on') : t('taskOptions.off') }));
  }
  if (opts.karaoke_style) {
    parts.push(t('taskOptions.karaoke', { value: String(opts.karaoke_style) }));
  }
  if (opts.prompt_preset) {
    parts.push(t('taskOptions.preset', { value: String(opts.prompt_preset) }));
  }
  if (opts.terminology_only) {
    parts.push(t('taskOptions.terminologyOnly'));
  }

  return parts.filter(Boolean).join(sep);
}

function formatPhaseMessage(message, t) {
  const raw = String(message || '').trim();
  if (!raw) return '';

  if (!raw.startsWith('Phase:')) return raw;
  const rest = raw.slice('Phase:'.length).trim();

  if (/^Downloading audio\b/i.test(rest)) return t('phase.downloadingAudio');
  if (/^Extracting audio\b/i.test(rest)) return t('phase.extractingAudio');
  if (/^Extracting terminology\b/i.test(rest)) return t('phase.extractingTerminology');
  if (/^Auditing terminology\b/i.test(rest)) return t('phase.auditingTerminology');

  if (/^Checking duration limit\b/i.test(rest)) {
    const m = rest.match(/\((\d+)\s*s\)/i);
    const seconds = m ? Number(m[1]) : null;
    if (Number.isFinite(seconds)) return t('phase.checkingDurationLimit', { seconds: Math.round(seconds) });
    return t('phase.checkingDurationLimit', { seconds: '?' });
  }
  if (/^Checking transcript length limit\b/i.test(rest)) {
    const m = rest.match(/\((\d+)\s*chars?\)/i);
    const chars = m ? Number(m[1]) : null;
    if (Number.isFinite(chars)) return t('phase.checkingTranscriptLimit', { chars: Math.round(chars) });
    return t('phase.checkingTranscriptLimit', { chars: '?' });
  }

  if (/^Transcribing\b/i.test(rest)) return t('phase.transcribing');
  if (/^Reading subtitle file\b/i.test(rest)) return t('phase.readingSubtitleFile');
  if (/^Preparing translation\b/i.test(rest)) return t('phase.preparingTranslation');
  if (/^Generating title\b/i.test(rest)) return t('phase.generatingTitle');

  if (/^Translating subtitles\b/i.test(rest)) return t('phase.translatingSubtitles');

  if (/^Translating\b/i.test(rest)) {
    const m = rest.match(/^Translating\s*\((\d+)\s*\/\s*(\d+)\)\s*to\s*([^(]+?)\s*\(DeepSeek\)/i);
    if (m) {
      const current = Number(m[1]);
      const total = Number(m[2]);
      const target = String(m[3] || '').trim();
      return t('phase.translating', {
        current: Number.isFinite(current) ? current : m[1],
        total: Number.isFinite(total) ? total : m[2],
        target: target || '?',
      });
    }
    return t('phase.translatingGeneric');
  }

  if (/^Generating ASS\b/i.test(rest)) {
    const m = rest.match(/^Generating ASS\s*\(([^)]+)\)/i);
    const target = m ? String(m[1] || '').trim() : '';
    return t('phase.generatingAss', { target: target || '?' });
  }

  if (/^Generating subtitles ZIP\b/i.test(rest)) return t('phase.generatingZip');

  // Fallback: strip the "Phase:" prefix so users don't see raw internal marker.
  return rest;
}

function formatPhase(task, t) {
  const status = String(task?.status || '').trim().toLowerCase();
  // When a task fails, always show the backend error message instead of the last phase code.
  const msg = String(task?.message || '').trim();
  if (status === 'failed' && msg) return msg;

  const phaseCode = String(task?.phase_code || '').trim();
  const meta = task?.phase_meta && typeof task.phase_meta === 'object' ? task.phase_meta : null;

  if (phaseCode) {
    if (phaseCode === 'cancelled') return t('phase.cancelled');
    if (phaseCode === 'downloading_audio') return t('phase.downloadingAudio');
    if (phaseCode === 'extracting_audio') return t('phase.extractingAudio');
    if (phaseCode === 'generating_karaoke_ass') {
      const lang = meta && typeof meta.lang === 'string' ? meta.lang : '';
      return t('phase.generatingKaraokeAss', { lang: lang || '?' });
    }
    if (phaseCode === 'extracting_terminology') return t('phase.extractingTerminology');
    if (phaseCode === 'auditing_terminology') return t('phase.auditingTerminology');
    if (phaseCode === 'checking_duration_limit') {
      const seconds = meta && Number.isFinite(Number(meta.seconds)) ? Math.round(Number(meta.seconds)) : '?';
      return t('phase.checkingDurationLimit', { seconds });
    }
    if (phaseCode === 'checking_transcript_limit') {
      const chars = meta && Number.isFinite(Number(meta.chars)) ? Math.round(Number(meta.chars)) : '?';
      return t('phase.checkingTranscriptLimit', { chars });
    }
    if (phaseCode === 'transcribing') return t('phase.transcribing');
    if (phaseCode === 'reading_subtitle_file') return t('phase.readingSubtitleFile');
    if (phaseCode === 'preparing_terminology') return t('phase.preparingTerminology');
    if (phaseCode === 'preparing_translation') return t('phase.preparingTranslation');
    if (phaseCode === 'generating_title') return t('phase.generatingTitle');
    if (phaseCode === 'translating_subtitles') return t('phase.translatingSubtitles');
    if (phaseCode === 'translating') {
      const current = meta && Number.isFinite(Number(meta.current)) ? Math.round(Number(meta.current)) : '?';
      const total = meta && Number.isFinite(Number(meta.total)) ? Math.round(Number(meta.total)) : '?';
      const target = meta && typeof meta.target === 'string' ? meta.target : '?';
      if (current !== '?' && total !== '?' && target && target !== '?') {
        return t('phase.translating', { current, total, target });
      }
      return t('phase.translatingGeneric');
    }
    if (phaseCode === 'generating_zip') return t('phase.generatingZip');
  }

  // Backward compatibility: fall back to best-effort parsing of message strings.
  return formatPhaseMessage(task?.message, t);
}

function App() {
  const { t, i18n } = useTranslation();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isLegacyReviewRoute = Boolean(pathname && pathname.startsWith('/subtranslator/review'));
  const isEditorRoute = Boolean(pathname && pathname.startsWith('/subtranslator/editor'));
  const isGlossaryRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/subtranslator/glossary');
  const [activeTab, setActiveTab] = useState('file'); // file | url | subtitle
  const [accessCode, setAccessCode] = useState(() => {
    try {
      return normalizeAccessCode(window.localStorage.getItem(ACCESS_CODE_STORAGE_KEY) || '');
    } catch (_) {
      return '';
    }
  });
  const [uiLanguageSetting, setUiLanguageSetting] = useState(() => {
    try {
      return window.localStorage.getItem(UI_LANGUAGE_STORAGE_KEY) || 'auto';
    } catch (_) {
      return 'auto';
    }
  });
  const [url, setUrl] = useState('');
  const [tasks, setTasks] = useState([]);
  const [cancellingTaskIds, setCancellingTaskIds] = useState({});
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminStats, setAdminStats] = useState(null);
  const [adminUpdatedAt, setAdminUpdatedAt] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedSubtitleFiles, setSelectedSubtitleFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadIndex, setUploadIndex] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [subUploading, setSubUploading] = useState(false);
  const [subUploadProgress, setSubUploadProgress] = useState(0);
  const [subUploadLabel, setSubUploadLabel] = useState('');
  const [subUploadIndex, setSubUploadIndex] = useState(0);
  const [subUploadTotal, setSubUploadTotal] = useState(0);
  const [targetLanguages, setTargetLanguages] = useState(['zh-Hans']);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [promptPresets, setPromptPresets] = useState([]);
  const [promptPreset, setPromptPreset] = useState('');
  const [customPromptsYaml, setCustomPromptsYaml] = useState('');
  const [glossaryYaml, setGlossaryYaml] = useState('');
  const [publicLimits, setPublicLimits] = useState(null);
  const [extractTerminology, setExtractTerminology] = useState(false);
  const [terminologyOnly, setTerminologyOnly] = useState(false);
  const [assEnabled, setAssEnabled] = useState(true);
  const [generateTitle, setGenerateTitle] = useState(true);
  const [karaokeStyle, setKaraokeStyle] = useState('cumulative_fill');
  const [sourceFont, setSourceFont] = useState('Segoe UI Semibold');
  const [sourceSize, setSourceSize] = useState(44);
  const [sourceColor, setSourceColor] = useState('#ffffff');
  const [highlightColor, setHighlightColor] = useState('#ffff00');
  const [sourceMarginV, setSourceMarginV] = useState(120);
  const [targetFont, setTargetFont] = useState('Microsoft YaHei UI');
  const [targetSize, setTargetSize] = useState(46);
  const [targetColor, setTargetColor] = useState('#ffffff');
  const [targetMarginV, setTargetMarginV] = useState(40);
  const fileInputRef = useRef(null);
  const subtitleInputRef = useRef(null);

  const selectedPromptPreset = promptPresets.find((p) => p.id === promptPreset);
  const customPreset = promptPresets.find((p) => p.id === 'custom');
  const customSampleYaml = typeof customPreset?.sample === 'string' ? customPreset.sample : '';

  // Keep <title> and meta description in sync for better SEO and share previews.
  useEffect(() => {
    try {
      document.title = t('seo.title');
      const desc = t('seo.description');
      const meta = document.querySelector('meta[name="description"]');
      if (meta && desc) meta.setAttribute('content', desc);
    } catch (_) {
      // no-op
    }
  }, [i18n.language, t]);

  const defaultPromptLabel = t('options.translationStyle.default');
  const defaultPromptDescription = t('options.translationStyle.defaultDescription');

  const currentPromptLabel = promptPreset ? selectedPromptPreset?.label || promptPreset : defaultPromptLabel;
  const currentPromptDescription = promptPreset
    ? selectedPromptPreset?.description || t('common.na')
    : defaultPromptDescription;

  useEffect(() => {
    if (promptPreset === 'custom' && !customPromptsYaml && customSampleYaml) {
      setCustomPromptsYaml(customSampleYaml);
    }
  }, [promptPreset, customPromptsYaml, customSampleYaml]);

  const handleCustomPromptFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      setCustomPromptsYaml(text);
    } catch (error) {
      console.error('Failed to read custom prompt file:', error);
      alert(t('error.promptFileReadFailed'));
    } finally {
      // Allow selecting the same file again.
      e.target.value = '';
    }
  };

  const handleGlossaryFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      setGlossaryYaml(text);
    } catch (error) {
      console.error('Failed to read glossary file:', error);
      alert(t('error.glossaryFileReadFailed'));
    } finally {
      e.target.value = '';
    }
  };

  const buildOptions = () => {
    const options = {
      target_languages: targetLanguages,
      ass_enabled: assEnabled,
      generate_title: generateTitle,
      karaoke_style: karaokeStyle,
      source_font: sourceFont,
      source_size: Number(sourceSize),
      source_color: sourceColor,
      highlight_color: highlightColor,
      source_margin_v: Number(sourceMarginV),
      target_font: targetFont,
      target_size: Number(targetSize),
      target_color: targetColor,
      target_margin_v: Number(targetMarginV),
    };
    if (promptPreset) options.prompt_preset = promptPreset;
    if (promptPreset === 'custom' && customPromptsYaml.trim()) options.custom_prompts_yaml = customPromptsYaml;
    if (glossaryYaml.trim()) options.glossary_yaml = glossaryYaml;
    if (extractTerminology) options.extract_terminology = true;
    if (terminologyOnly) {
      // Terminology-only mode (extract candidates without running translation/ASS/title).
      options.terminology_only = true;
      options.extract_terminology = true;
      options.ass_enabled = false;
      options.generate_title = false;
    }
    return options;
  };

  const toggleTargetLanguage = (code) => {
    setTargetLanguages((prev) =>
      prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]
    );
  };

  const recommendedLanguages = languageOptions.filter((lang) => !lang.tier || lang.tier === 'high');
  const otherLanguages = languageOptions.filter((lang) => lang.tier && lang.tier !== 'high');

  const fetchTasks = useCallback(async () => {
    try {
      const response = await getAllTasks();
      // Prefer showing newest first (backend dict is insertion-ordered but not guaranteed).
      const list = Array.isArray(response.data) ? response.data : [];
      setTasks(list.slice().reverse());
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, []);

  const handleCancelTask = useCallback(
    async (taskId) => {
      const id = String(taskId || '').trim();
      if (!id) return;
      setCancellingTaskIds((prev) => ({ ...(prev || {}), [id]: true }));
      try {
        await cancelTask(id);
        fetchTasks();
      } catch (error) {
        console.error('Cancel task failed:', error);
        alert(t('error.taskCancelFailed'));
      } finally {
        setCancellingTaskIds((prev) => {
          const next = { ...(prev || {}) };
          delete next[id];
          return next;
        });
      }
    },
    [fetchTasks, t]
  );

  const handleRetryTask = useCallback(
    async (taskId) => {
      const id = String(taskId || '').trim();
      if (!id) return;
      try {
        await retryTask(id);
        fetchTasks();
      } catch (error) {
        console.error('Retry task failed:', error);
        alert(t('error.taskRetryFailed'));
      }
    },
    [fetchTasks, t]
  );

  const handleDeleteTask = useCallback(
    async (taskId) => {
      const id = String(taskId || '').trim();
      if (!id) return;
      // Confirm destructive action.
      const ok = window.confirm(t('confirm.deleteTask'));
      if (!ok) return;
      try {
        await deleteTask(id);
        fetchTasks();
      } catch (error) {
        console.error('Delete task failed:', error);
        alert(t('error.taskDeleteFailed'));
      }
    },
    [fetchTasks, t]
  );

  useEffect(() => {
    fetchTasks();
    if (isLegacyReviewRoute || isEditorRoute || isGlossaryRoute) return undefined;
    const interval = setInterval(fetchTasks, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [fetchTasks, isLegacyReviewRoute, isEditorRoute, isGlossaryRoute]);

  const fetchLanguages = useCallback(async () => {
    try {
      const response = await getLanguages();
      const list = Array.isArray(response.data) ? response.data : [];
      setLanguageOptions(list.length > 0 ? list : FALLBACK_LANGUAGE_OPTIONS);
    } catch (error) {
      console.error('Error fetching languages:', error);
      setLanguageOptions(FALLBACK_LANGUAGE_OPTIONS);
    }
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const fetchLimits = useCallback(async () => {
    try {
      const response = await getLimits();
      setPublicLimits(response.data || null);
    } catch (error) {
      console.error('Error fetching limits:', error);
      setPublicLimits(null);
    }
  }, []);

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  const fetchPromptPresets = useCallback(async () => {
    try {
      const response = await getPromptPresets();
      const list = Array.isArray(response.data) ? response.data : [];
      setPromptPresets(list);
    } catch (error) {
      console.error('Error fetching prompt presets:', error);
      setPromptPresets([]);
    }
  }, []);

  useEffect(() => {
    fetchPromptPresets();
  }, [fetchPromptPresets]);

  const fetchAdminDashboardStats = useCallback(async () => {
    let code = '';
    try {
      code = window.localStorage.getItem(ACCESS_CODE_STORAGE_KEY) || '';
    } catch (_) {
      code = accessCode || '';
    }
    if (!code) {
      setAdminStats(null);
      setAdminError(t('error.adminNeedAccessCode'));
      return;
    }

    setAdminLoading(true);
    setAdminError('');
    try {
      const response = await getAdminStats();
      setAdminStats(response.data || null);
      setAdminUpdatedAt(Date.now());
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      const status = error?.response?.status;
      if (status === 401) setAdminError(t('error.unauthorizedCheckAccessCode'));
      else setAdminError(t('error.failedLoadAdminStats'));
      setAdminStats(null);
    } finally {
      setAdminLoading(false);
    }
  }, [accessCode, t]);

  useEffect(() => {
    if (!adminOpen) return;
    fetchAdminDashboardStats();
  }, [adminOpen, fetchAdminDashboardStats]);

  useEffect(() => {
    if (!adminOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setAdminOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [adminOpen]);

  const uploadVideoFilesSequentially = async (files) => {
    if (!files || files.length === 0) return;
    if (uploading) return;

    // Snapshot options so a user changing options mid-upload doesn't affect the queue.
    const optionsSnapshot = buildOptions();

    setUploading(true);
    setUploadTotal(files.length);
    try {
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        setUploadIndex(i + 1);
        setUploadLabel(f.name);
        setUploadProgress(0);

        await uploadVideoWithOptions(
          f,
          (progressEvent) => {
            const total = progressEvent.total || 0;
            const percentCompleted = total ? Math.round((progressEvent.loaded * 100) / total) : 0;
            setUploadProgress(percentCompleted);
          },
          optionsSnapshot
        );
        await fetchTasks();
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      const status = error?.response?.status;
      if (status === 401) alert(t('error.unauthorizedCheckAccessCode'));
      else alert(t('error.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadLabel('');
      setUploadIndex(0);
      setUploadTotal(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setSelectedFiles([]);
    }
  };

  const uploadSubtitleFilesSequentially = async (files) => {
    if (!files || files.length === 0) return;
    if (subUploading) return;

    // Subtitle-only endpoint currently only supports target_languages.
    const optionsSnapshot = { target_languages: targetLanguages };
    if (promptPreset) optionsSnapshot.prompt_preset = promptPreset;
    if (promptPreset === 'custom' && customPromptsYaml.trim()) optionsSnapshot.custom_prompts_yaml = customPromptsYaml;

    setSubUploading(true);
    setSubUploadTotal(files.length);
    try {
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        setSubUploadIndex(i + 1);
        setSubUploadLabel(f.name);
        setSubUploadProgress(0);

        await uploadSubtitleWithOptions(
          f,
          (progressEvent) => {
            const total = progressEvent.total || 0;
            const percentCompleted = total ? Math.round((progressEvent.loaded * 100) / total) : 0;
            setSubUploadProgress(percentCompleted);
          },
          optionsSnapshot
        );
        await fetchTasks();
      }
    } catch (error) {
      console.error('Error uploading subtitle files:', error);
      const status = error?.response?.status;
      if (status === 401) alert(t('error.unauthorizedCheckAccessCode'));
      else alert(t('error.subtitleUploadFailed'));
    } finally {
      setSubUploading(false);
      setSubUploadProgress(0);
      setSubUploadLabel('');
      setSubUploadIndex(0);
      setSubUploadTotal(0);
      if (subtitleInputRef.current) subtitleInputRef.current.value = '';
      setSelectedSubtitleFiles([]);
    }
  };

  const handleFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    const isVideo = (f) => {
      const t = String(f?.type || '').toLowerCase();
      if (t.startsWith('video/')) return true;
      const name = String(f?.name || '').toLowerCase();
      return VIDEO_EXTS.some((ext) => name.endsWith(ext));
    };

    const rejected = list.filter(isVideo);
    const accepted = list.filter((f) => !isVideo(f));

    if (rejected.length > 0) {
      const names = rejected
        .slice(0, 5)
        .map((f) => f.name)
        .join(', ');
      const more = rejected.length > 5 ? '…' : '';
      alert(`${t('error.videoNotSupported')}\n${t('error.rejected', { names, more })}`);
    }

    setSelectedFiles(accepted);
  };

  const handleSubFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    setSelectedSubtitleFiles(list);
  };

  const handleUrlChange = (e) => {
    setUrl(e.target.value);
  };

  const handleAccessCodeChange = (e) => {
    const value = normalizeAccessCode(e.target.value);
    setAccessCode(value);
    try {
      if (value) window.localStorage.setItem(ACCESS_CODE_STORAGE_KEY, value);
      else window.localStorage.removeItem(ACCESS_CODE_STORAGE_KEY);
    } catch (_) {
      // Ignore localStorage failures (private mode, blocked storage, etc.)
    }
  };

  const _detectUiLanguageFromBrowser = () => {
    try {
      const langs = Array.isArray(window.navigator?.languages) && window.navigator.languages.length > 0
        ? window.navigator.languages
        : [window.navigator?.language];
      for (const raw of langs) {
        const mapped = mapBrowserLanguageToSupported(raw);
        if (mapped === 'en' || mapped === 'zh-Hans' || mapped === 'zh-Hant') return mapped;
      }
    } catch (_) {
      // Ignore browser detection failures.
    }
    return 'en';
  };

  const handleUiLanguageChange = async (e) => {
    const value = String(e.target.value || '').trim();
    if (!value) return;

    if (value === 'auto') {
      try {
        window.localStorage.removeItem(UI_LANGUAGE_STORAGE_KEY);
      } catch (_) {
        // Ignore localStorage failures.
      }
      setUiLanguageSetting('auto');
      await i18n.changeLanguage(_detectUiLanguageFromBrowser());
      return;
    }

    try {
      window.localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, value);
    } catch (_) {
      // Ignore localStorage failures.
    }
    setUiLanguageSetting(value);
    await i18n.changeLanguage(value);
  };

  const handleProcessUrl = async () => {
    if (!url) return;
    try {
      await processVideoFromUrlWithOptions(url, buildOptions());
      setUrl('');
      fetchTasks();
    } catch (error) {
      console.error('Error processing URL:', error);
      const status = error?.response?.status;
      if (status === 401) alert(t('error.unauthorizedCheckAccessCode'));
      else alert(t('error.urlProcessingFailed'));
    }
  };

  const formatCompactNumber = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return t('common.na');
    const abs = Math.abs(n);
    if (abs < 1000) return String(Math.round(n));
    if (abs < 1e6) return `${(n / 1e3).toFixed(abs < 1e4 ? 1 : 0)}k`;
    if (abs < 1e9) return `${(n / 1e6).toFixed(abs < 1e7 ? 1 : 0)}M`;
    return `${(n / 1e9).toFixed(1)}B`;
  };

  const formatMinutes = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return t('common.na');
    const abs = Math.abs(n);
    const unit = t('units.minuteShort');
    if (abs < 1) return `${n.toFixed(2)} ${unit}`;
    if (abs < 10) return `${n.toFixed(1)} ${unit}`;
    return `${Math.round(n)} ${unit}`;
  };

  const formatUSD = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return t('common.na');
    if (n === 0) return '$0.00';
    if (n < 0.01) return `$${n.toFixed(4)}`;
    if (n < 1) return `$${n.toFixed(3)}`;
    return `$${n.toFixed(2)}`;
  };

  const estimateDeepseekCost = (inputTokens, outputTokens) => {
    const inTok = Number(inputTokens);
    const outTok = Number(outputTokens);
    const totalTokens = (Number.isFinite(inTok) ? inTok : 0) + (Number.isFinite(outTok) ? outTok : 0);
    return (totalTokens / 1e6) * DEEPSEEK_USD_PER_1M_TOKENS;
  };

  const estimateDeepgramCost = (audioMinutes) => {
    const mins = Number(audioMinutes);
    return (Number.isFinite(mins) ? mins : 0) * DEEPGRAM_USD_PER_AUDIO_MINUTE;
  };

  const adminUpdatedLabel = adminUpdatedAt ? new Date(adminUpdatedAt).toLocaleString() : '';
  const anonLimits = publicLimits && typeof publicLimits === 'object' ? publicLimits.anon : null;
  const anonHuman = publicLimits && typeof publicLimits === 'object' ? publicLimits.anon_human : null;
  const anonMaxMinutes = (() => {
    const seconds = Number(anonLimits?.max_duration_seconds);
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    return Math.round(seconds / 60);
  })();

  // Backward compatibility: /subtranslator/review -> /subtranslator/editor
  useEffect(() => {
    if (!isLegacyReviewRoute) return;
    try {
      const url = new URL(window.location.href);
      url.pathname = '/subtranslator/editor';
      window.location.replace(url.toString());
    } catch (_) {
      window.location.replace('/subtranslator/editor');
    }
  }, [isLegacyReviewRoute]);

  return (
    <div className="App">
      <header className="App-header">
        <div className="Brand">
          <div className="Brand-title">gongju.gold</div>
          <div className="Brand-subtitle">{t('brand.subtitle')}</div>
        </div>
        <div className="Header-meta">{t('brand.meta')}</div>
      </header>
      <main>
        {isLegacyReviewRoute ? (
          <div className="Empty">{t('editor.redirecting')}</div>
        ) : isEditorRoute ? (
          <EditorPage tasks={tasks} onRefreshTasks={fetchTasks} />
        ) : isGlossaryRoute ? (
          <GlossaryEditorPage tasks={tasks} onRefreshTasks={fetchTasks} />
        ) : (
        <div className="Layout">
          <section className="Column">
            <div className="Card">
              <div className="CardHeader">
                <h2 className="CardTitle">{t('createTask.title')}</h2>
                <div className="CardHint">{t('createTask.hint')}</div>
              </div>

              <div className="Tabs" role="tablist" aria-label={t('tabs.ariaLabel')}>
                <button
                  type="button"
                  className={`Tab ${activeTab === 'file' ? 'Tab--active' : ''}`}
                  onClick={() => setActiveTab('file')}
                  aria-selected={activeTab === 'file'}
                  role="tab"
                >
                  {t('tabs.uploadFile')}
                </button>
                <button
                  type="button"
                  className={`Tab ${activeTab === 'url' ? 'Tab--active' : ''}`}
                  onClick={() => setActiveTab('url')}
                  aria-selected={activeTab === 'url'}
                  role="tab"
                >
                  {t('tabs.fromUrl')}
                </button>
                <button
                  type="button"
                  className={`Tab ${activeTab === 'subtitle' ? 'Tab--active' : ''}`}
                  onClick={() => setActiveTab('subtitle')}
                  aria-selected={activeTab === 'subtitle'}
                  role="tab"
                >
                  {t('tabs.translateSrt')}
                </button>
              </div>

              {activeTab === 'file' && (
                <div className="Form">
                  <label className="Field">
                    <div className="FieldLabel">{t('fileTab.audioFiles')}</div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileChange}
                      accept="audio/*"
                      multiple
                      disabled={uploading}
                    />
                  </label>
                  <div className="SmallNote">{t('fileTab.hint')}</div>
                  {anonLimits && (
                    <div className="SmallNote">
                      {t('limits.fileAnon', {
                        minutes: anonMaxMinutes != null ? anonMaxMinutes : t('common.na'),
                        chars: anonLimits.max_transcript_chars != null ? anonLimits.max_transcript_chars : t('common.na'),
                        upload: anonHuman?.max_audio_upload || t('common.na'),
                      })}
                    </div>
                  )}
                  {selectedFiles.length > 0 && (
                    <div className="SmallNote">{t('fileTab.selected', { count: selectedFiles.length })}</div>
                  )}
                  <div className="Row">
                    <button
                      className="Btn Btn--primary"
                      type="button"
                      onClick={() => uploadVideoFilesSequentially(selectedFiles)}
                      disabled={uploading || selectedFiles.length === 0}
                    >
                      {t('button.start')}
                    </button>
                    <button className="Btn Btn--ghost" type="button" onClick={fetchTasks} disabled={uploading}>
                      {t('button.refresh')}
                    </button>
                  </div>
                  {uploading && (
                    <>
                      <div className="SmallNote">
                        {t('fileTab.uploading', { index: uploadIndex, total: uploadTotal, name: uploadLabel })}
                      </div>
                      <progress value={uploadProgress} max="100" />
                    </>
                  )}
                </div>
              )}

              {activeTab === 'url' && (
                <div className="Form">
                  <label className="Field">
                    <div className="FieldLabel">{t('urlTab.videoUrl')}</div>
                    <input
                      type="text"
                      value={url}
                      onChange={handleUrlChange}
                      placeholder={t('urlTab.placeholder')}
                    />
                  </label>
                  <div className="Row">
                    <button className="Btn Btn--primary" onClick={handleProcessUrl} disabled={!url}>
                      {t('button.start')}
                    </button>
                    <button className="Btn Btn--ghost" type="button" onClick={fetchTasks}>
                      {t('button.refresh')}
                    </button>
                  </div>
                  <div className="SmallNote">
                    {t('urlTab.tip')}
                  </div>
                  {anonLimits && (
                    <div className="SmallNote">
                      {t('limits.urlAnon', {
                        minutes: anonMaxMinutes != null ? anonMaxMinutes : t('common.na'),
                        chars: anonLimits.max_transcript_chars != null ? anonLimits.max_transcript_chars : t('common.na'),
                        filesize: anonHuman?.max_url_filesize || t('common.na'),
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'subtitle' && (
                <div className="Form">
                  <label className="Field">
                    <div className="FieldLabel">{t('subtitleTab.srtFiles')}</div>
                    <input
                      ref={subtitleInputRef}
                      type="file"
                      onChange={handleSubFileChange}
                      accept=".srt"
                      multiple
                      disabled={subUploading}
                    />
                  </label>
                  <div className="SmallNote">{t('subtitleTab.hint')}</div>
                  {anonLimits && (
                    <div className="SmallNote">
                      {t('limits.subtitleAnon', {
                        chars: anonLimits.max_subtitle_chars != null ? anonLimits.max_subtitle_chars : t('common.na'),
                        upload: anonHuman?.max_subtitle_upload || t('common.na'),
                      })}
                    </div>
                  )}
                  {selectedSubtitleFiles.length > 0 && (
                    <div className="SmallNote">{t('subtitleTab.selected', { count: selectedSubtitleFiles.length })}</div>
                  )}
                  <div className="Row">
                    <button
                      className="Btn Btn--primary"
                      type="button"
                      onClick={() => uploadSubtitleFilesSequentially(selectedSubtitleFiles)}
                      disabled={subUploading || selectedSubtitleFiles.length === 0}
                    >
                      {t('button.start')}
                    </button>
                    <button className="Btn Btn--ghost" type="button" onClick={fetchTasks} disabled={subUploading}>
                      {t('button.refresh')}
                    </button>
                  </div>
                  {subUploading && (
                    <>
                      <div className="SmallNote">
                        {t('subtitleTab.uploading', { index: subUploadIndex, total: subUploadTotal, name: subUploadLabel })}
                      </div>
                      <progress value={subUploadProgress} max="100" />
                    </>
                  )}
                  <div className="SmallNote">{t('subtitleTab.noteOnlySrt')}</div>
                </div>
              )}
            </div>

            <div className="Card">
              <div className="CardHeader">
                <h2 className="CardTitle">{t('options.title')}</h2>
                <div className="CardHint">{t('options.hint')}</div>
              </div>

              <div className="Section">
                <div className="SectionTitle">{t('options.targets.title')}</div>
                <div className="SmallNote">{t('options.targets.hint')}</div>
                <div className="LangGrid">
                  {recommendedLanguages.map((lang) => (
                    <label key={lang.code} className="Check">
                      <input
                        type="checkbox"
                        checked={targetLanguages.includes(lang.code)}
                        onChange={() => toggleTargetLanguage(lang.code)}
                      />
                      <span>{lang.name}</span>
                    </label>
                  ))}
                </div>
                {otherLanguages.length > 0 && (
                  <details className="Details">
                    <summary>{t('options.targets.more')}</summary>
                    <div className="LangGrid">
                      {otherLanguages.map((lang) => (
                        <label key={lang.code} className="Check">
                          <input
                            type="checkbox"
                            checked={targetLanguages.includes(lang.code)}
                            onChange={() => toggleTargetLanguage(lang.code)}
                          />
                          <span>{lang.name}</span>
                          <span className="Code">
                            {lang.tier === 'medium' ? t('options.targets.tierMedium') : t('options.targets.tierLow')}
                          </span>
                        </label>
                      ))}
                    </div>
                  </details>
                )}
                {targetLanguages.length === 0 && (
                  <div className="SmallNote">{t('options.targets.requireOne')}</div>
                )}
              </div>

              {promptPresets.length > 0 && (
                <div className="Section">
                  <div className="SectionTitle">{t('options.translationStyle.title')}</div>
                  <label className="Field Field--inline">
                    <span className="FieldLabel">{t('options.translationStyle.presetLabel')}</span>
                    <select
                      value={promptPreset}
                      onChange={(e) => setPromptPreset(e.target.value)}
                      aria-label={t('options.translationStyle.presetLabel')}
                    >
                      <option value="">{t('options.translationStyle.default')}</option>
                      {promptPresets.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="SmallNote">
                    {t('options.translationStyle.current', {
                      label: currentPromptLabel,
                      description: currentPromptDescription,
                    })}
                  </div>

                  {promptPreset === 'custom' && (
                    <div className="Section">
                      <div className="SectionTitle">{t('options.translationStyle.custom.title')}</div>
                      <div className="SmallNote">
                        {t('options.translationStyle.custom.hint')}
                      </div>

                      <label className="Field">
                        <div className="FieldLabel">{t('options.translationStyle.custom.uploadLabel')}</div>
                        <input type="file" accept=".yaml,.yml,.txt" onChange={handleCustomPromptFileChange} />
                      </label>

                      <div className="Row Row--wrap">
                        <button
                          className="Btn Btn--ghost Btn--small"
                          type="button"
                          onClick={() => setCustomPromptsYaml(customSampleYaml || '')}
                          disabled={!customSampleYaml}
                        >
                          {t('button.resetToSample')}
                        </button>
                        <button
                          className="Btn Btn--ghost Btn--small"
                          type="button"
                          onClick={() => setCustomPromptsYaml('')}
                          disabled={!customPromptsYaml}
                        >
                          {t('button.clear')}
                        </button>
                      </div>

                      <label className="Field">
                        <div className="FieldLabel">{t('options.translationStyle.custom.yamlLabel')}</div>
                        <textarea
                          value={customPromptsYaml}
                          onChange={(e) => setCustomPromptsYaml(e.target.value)}
                          rows={14}
                          placeholder={customSampleYaml || t('options.translationStyle.custom.placeholder')}
                        />
                      </label>
                      <div className="SmallNote">{t('options.translationStyle.custom.tip')}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="Section">
                <div className="SectionTitle">{t('options.glossary.title')}</div>
                <div className="SmallNote">{t('options.glossary.hint')}</div>

                <div className="Row Row--wrap Row--center">
                  <Tooltip text={t('options.glossary.extractNote')}>
                    <span className="Row Row--center">
                      <label className="Check">
                        <input
                          type="checkbox"
                          checked={extractTerminology}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setExtractTerminology(checked);
                            if (!checked) setTerminologyOnly(false);
                          }}
                        />
                        <span>{t('options.glossary.extractToggle')}</span>
                      </label>
                      <span className="InfoIcon" aria-label={t('options.glossary.extractNote')} tabIndex={0}>
                        ?
                      </span>
                    </span>
                  </Tooltip>

                  <Tooltip text={t('options.glossary.terminologyOnlyNote')}>
                    <span className="Row Row--center">
                      <label className="Check">
                        <input
                          type="checkbox"
                          checked={terminologyOnly}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setTerminologyOnly(checked);
                            if (checked) setExtractTerminology(true);
                          }}
                        />
                        <span>{t('options.glossary.terminologyOnlyToggle')}</span>
                      </label>
                      <span className="InfoIcon" aria-label={t('options.glossary.terminologyOnlyNote')} tabIndex={0}>
                        ?
                      </span>
                    </span>
                  </Tooltip>
                </div>

                <label className="Field">
                  <div className="FieldLabel">{t('options.glossary.uploadLabel')}</div>
                  <input type="file" accept=".yaml,.yml,.json,.txt" onChange={handleGlossaryFileChange} />
                </label>

                <div className="Row Row--wrap">
                  <button
                    className="Btn Btn--ghost Btn--small"
                    type="button"
                    onClick={() => setGlossaryYaml(GLOSSARY_SAMPLE_YAML)}
                  >
                    {t('button.resetToSample')}
                  </button>
                  <button
                    className="Btn Btn--ghost Btn--small"
                    type="button"
                    onClick={() => {
                      try {
                        window.localStorage.setItem('gongju_glossary_editor_seed_v1', glossaryYaml || '');
                      } catch (_) {
                        // ignore
                      }
                      window.open('/subtranslator/glossary', '_blank', 'noopener,noreferrer');
                    }}
                  >
                    {t('button.editGlossary')}
                  </button>
                  <button
                    className="Btn Btn--ghost Btn--small"
                    type="button"
                    onClick={() => setGlossaryYaml('')}
                    disabled={!glossaryYaml}
                  >
                    {t('button.clear')}
                  </button>
                </div>

                <label className="Field">
                  <div className="FieldLabel">{t('options.glossary.yamlLabel')}</div>
                  <textarea
                    value={glossaryYaml}
                    onChange={(e) => setGlossaryYaml(e.target.value)}
                    rows={12}
                    placeholder={t('options.glossary.placeholder')}
                  />
                </label>
              </div>

              <div className="Section">
                <div className="SectionTitle">{t('options.outputs.title')}</div>
                <label className="Check">
                  <input
                    type="checkbox"
                    checked={assEnabled}
                    disabled={terminologyOnly}
                    onChange={(e) => setAssEnabled(e.target.checked)}
                  />
                  <span>{t('options.outputs.ass')}</span>
                </label>
                <label className="Check">
                  <input
                    type="checkbox"
                    checked={generateTitle}
                    disabled={terminologyOnly}
                    onChange={(e) => setGenerateTitle(e.target.checked)}
                  />
                  <span>{t('options.outputs.titleGen')}</span>
                </label>
                <div className="SmallNote">{t('options.outputs.note')}</div>
              </div>

              <details className="Details">
                <summary>{t('options.assDetails.summary')}</summary>
                <div className="Section">
                  <label className="Field Field--inline">
                    <span className="FieldLabel">{t('options.assDetails.karaokeStyle')}</span>
                    <select value={karaokeStyle} onChange={(e) => setKaraokeStyle(e.target.value)}>
                      <option value="cumulative_fill">{t('options.assDetails.style.cumulativeFill')}</option>
                      <option value="word_highlight">{t('options.assDetails.style.wordHighlight')}</option>
                    </select>
                  </label>

                  <div className="TwoCol">
                    <div>
                      <div className="SectionTitle">{t('options.assDetails.sourceTitle')}</div>
                      <label className="Field">
                        <div className="FieldLabel">{t('options.assDetails.font')}</div>
                        <input value={sourceFont} onChange={(e) => setSourceFont(e.target.value)} />
                      </label>
                      <div className="Row">
                        <label className="Field">
                          <div className="FieldLabel">{t('options.assDetails.size')}</div>
                          <input type="number" value={sourceSize} onChange={(e) => setSourceSize(e.target.value)} />
                        </label>
                        <label className="Field">
                          <div className="FieldLabel">{t('options.assDetails.marginV')}</div>
                          <input type="number" value={sourceMarginV} onChange={(e) => setSourceMarginV(e.target.value)} />
                        </label>
                      </div>
                      <div className="Row">
                        <label className="Field">
                          <div className="FieldLabel">{t('options.assDetails.color')}</div>
                          <input type="color" value={sourceColor} onChange={(e) => setSourceColor(e.target.value)} />
                        </label>
                        <label className="Field">
                          <div className="FieldLabel">{t('options.assDetails.highlight')}</div>
                          <input type="color" value={highlightColor} onChange={(e) => setHighlightColor(e.target.value)} />
                        </label>
                      </div>
                    </div>
                    <div>
                      <div className="SectionTitle">{t('options.assDetails.targetTitle')}</div>
                      <label className="Field">
                        <div className="FieldLabel">{t('options.assDetails.font')}</div>
                        <input value={targetFont} onChange={(e) => setTargetFont(e.target.value)} />
                      </label>
                      <div className="Row">
                        <label className="Field">
                          <div className="FieldLabel">{t('options.assDetails.size')}</div>
                          <input type="number" value={targetSize} onChange={(e) => setTargetSize(e.target.value)} />
                        </label>
                        <label className="Field">
                          <div className="FieldLabel">{t('options.assDetails.marginV')}</div>
                          <input type="number" value={targetMarginV} onChange={(e) => setTargetMarginV(e.target.value)} />
                        </label>
                      </div>
                      <label className="Field">
                        <div className="FieldLabel">{t('options.assDetails.color')}</div>
                        <input type="color" value={targetColor} onChange={(e) => setTargetColor(e.target.value)} />
                      </label>
                    </div>
                  </div>
                </div>
              </details>
            </div>
          </section>

          <section className="Card Sidebar">
            <div className="CardHeader Row Row--between Row--center">
              <div>
                <h2 className="CardTitle">{t('tasks.title')}</h2>
                <div className="CardHint">{t('tasks.hint', { count: tasks.length })}</div>
              </div>
              <button className="Btn Btn--ghost Btn--small" type="button" onClick={fetchTasks}>
                {t('button.refresh')}
              </button>
            </div>

            {tasks.length === 0 ? (
              <div className="Empty">{t('tasks.empty')}</div>
            ) : (
              <div className="TaskList">
                {tasks.map((task) => (
                  <div key={task.id} className="TaskCard">
                    <div className="Row Row--between Row--center">
                      <div className="TaskTitle">
                        {task.input_name || task.input_url || task.id}
                        <div className="TaskMeta">{task.id}</div>
                      </div>
                      <span className={`Pill Pill--${task.status}`}>{formatStatusLabel(task.status, t)}</span>
                    </div>
                    <div className="TaskMessage">
                      {task.message || task.phase_code ? formatPhase(task, t) : t('common.na')}
                    </div>
                    {Array.isArray(task.warnings) && task.warnings.length > 0 && (
                      <div className="TaskWarnings">{task.warnings.join(' | ')}</div>
                    )}
                    {task.options && (
                      <div className="TaskOptions">{formatTaskOptionsLabel(task.options, t)}</div>
                    )}

                    <div className="Chips">
                      {Array.isArray(task.outputs) && task.outputs.length > 0 ? (
                        task.outputs.flatMap((out) => {
                          const filename = String(out?.filename || '').toLowerCase();
                          if (out.kind === 'glossary_candidates' && !filename.endsWith('.json')) return [];

                          const chips = [
                            (
                              <a
                                key={`${task.id}-${out.kind}-${out.filename}`}
                                className="Chip"
                                href={out.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {out.label || out.filename}
                              </a>
                            ),
                          ];

                          if (out.kind === 'glossary_candidates' && out.url) {
                            chips.push(
                              (
                                <a
                                  key={`${task.id}-${out.kind}-${out.filename}-edit`}
                                  className="Chip"
                                  href={`/subtranslator/glossary?src=${encodeURIComponent(out.url)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t('button.editGlossary')}
                                </a>
                              )
                            );
                          }
                          return chips;
                        })
                      ) : (
                        <>
                          {task.result_url && (
                            <a className="Chip" href={task.result_url} target="_blank" rel="noopener noreferrer">
                              {t('button.download')}
                            </a>
                          )}
                          {task.source_url && (
                            <a className="Chip" href={task.source_url} target="_blank" rel="noopener noreferrer">
                              {t('button.sourceTranscript')}
                            </a>
                          )}
                        </>
                      )}

                      {task.status === 'completed' && (
                        <>
                          <a
                            className="Chip"
                            href={`/tasks/${task.id}/subtitles.zip`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('button.downloadAllSubtitlesZip')}
                          </a>
                          <a
                            className="Chip"
                            href={`/subtranslator/editor?task=${encodeURIComponent(task.id)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('button.editor')}
                          </a>
                        </>
                      )}

                      {(task.status === 'pending' || task.status === 'queued' || task.status === 'processing') && (
                        <button
                          className="Chip Chip--danger"
                          type="button"
                          disabled={Boolean(cancellingTaskIds && cancellingTaskIds[task.id])}
                          onClick={() => handleCancelTask(task.id)}
                        >
                          {Boolean(cancellingTaskIds && cancellingTaskIds[task.id])
                            ? t('button.cancelling')
                            : t('button.cancel')}
                        </button>
                      )}
                      {(task.status === 'failed' || task.status === 'cancelled') && (
                        <button className="Chip" type="button" onClick={() => handleRetryTask(task.id)}>
                          {t('button.retry')}
                        </button>
                      )}
                      {task.status !== 'processing' && (
                        <button className="Chip Chip--danger" type="button" onClick={() => handleDeleteTask(task.id)}>
                          {t('button.delete')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        )}
      </main>
      <footer className="App-footer">
        <p>{t('footer.poweredBy')}</p>
        <input
          className="FooterInput"
          type="text"
          value={accessCode}
          onChange={handleAccessCodeChange}
          placeholder={t('footer.adminCode.placeholder')}
          aria-label={t('footer.adminCode.label')}
          autoComplete="off"
        />
        <select
          className="FooterSelect"
          value={uiLanguageSetting || 'auto'}
          onChange={handleUiLanguageChange}
          aria-label={t('language.label')}
        >
          <option value="auto">{t('language.auto')}</option>
          <option value="en">{t('language.english')}</option>
          <option value="zh-Hans">{t('language.zhHans')}</option>
          <option value="zh-Hant">{t('language.zhHant')}</option>
        </select>
        <button type="button" className="AdminLink" onClick={() => setAdminOpen(true)}>
          {t('button.admin')}
        </button>
      </footer>

      {adminOpen && (
        <div
          className="ModalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('admin.ariaLabel')}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setAdminOpen(false);
          }}
        >
          <div className="Modal">
            <div className="ModalHeader Row Row--between Row--center">
              <div>
                <div className="ModalTitle">{t('admin.title')}</div>
                <div className="SmallNote">
                  {adminUpdatedLabel ? t('admin.updated', { time: adminUpdatedLabel }) : t('common.na')}
                </div>
              </div>
              <div className="Row">
                <button
                  className="Btn Btn--ghost Btn--small"
                  type="button"
                  onClick={fetchAdminDashboardStats}
                  disabled={adminLoading}
                >
                  {t('button.refresh')}
                </button>
                <button className="Btn Btn--ghost Btn--small" type="button" onClick={() => setAdminOpen(false)}>
                  {t('button.close')}
                </button>
              </div>
            </div>

            <div className="ModalBody">
              {adminError && <div className="AdminAlert AdminAlert--error">{adminError}</div>}
              {adminLoading && <div className="AdminAlert">{t('admin.loading')}</div>}

              {adminStats && (
                <>
                  <div className="AdminGrid">
                    <div className="AdminCard">
                      <div className="AdminCardLabel">{t('admin.queueDepth')}</div>
                      <div className="AdminCardValue">{adminStats.queue_depth ?? t('common.na')}</div>
                    </div>
                    <div className="AdminCard">
                      <div className="AdminCardLabel">{t('admin.freeDisk')}</div>
                      <div className="AdminCardValue">
                        {adminStats.system_info?.free_gb != null ? `${adminStats.system_info.free_gb} GB` : t('common.na')}
                      </div>
                    </div>
                  </div>

                  <div className="AdminSection">
                    <div className="AdminSectionTitle">{t('admin.taskCounts')}</div>
                    <div className="AdminBadges">
                      <span className="Badge Badge--ok">
                        {t('admin.completed', { count: adminStats.task_counts?.completed ?? 0 })}
                      </span>
                      <span className="Badge Badge--warn">
                        {t('admin.processing', { count: adminStats.task_counts?.processing ?? 0 })}
                      </span>
                      <span className="Badge Badge--danger">
                        {t('admin.failed', { count: adminStats.task_counts?.failed ?? 0 })}
                      </span>
                    </div>
                  </div>

                  <div className="AdminSection">
                    <div className="AdminSectionTitle">{t('admin.costUsage')}</div>
                    <div className="AdminList">
                      <div className="AdminListRow">
                        <span>{t('admin.deepseekInputTokens')}</span>
                        <span>
                          {formatCompactNumber(adminStats.usage?.today?.input_tokens)} /{' '}
                          {formatCompactNumber(adminStats.usage?.total?.input_tokens)}
                        </span>
                      </div>
                      <div className="AdminListRow">
                        <span>{t('admin.deepseekOutputTokens')}</span>
                        <span>
                          {formatCompactNumber(adminStats.usage?.today?.output_tokens)} /{' '}
                          {formatCompactNumber(adminStats.usage?.total?.output_tokens)}
                        </span>
                      </div>
                      <div className="AdminListRow">
                        <span>{t('admin.deepgramAudio')}</span>
                        <span>
                          {formatMinutes(adminStats.usage?.today?.audio_minutes)} /{' '}
                          {formatMinutes(adminStats.usage?.total?.audio_minutes)}
                        </span>
                      </div>
                      <div className="AdminListRow">
                        <span>{t('admin.estCost')}</span>
                        <span>
                          {t('admin.today')}:{' '}
                          {formatUSD(
                            estimateDeepseekCost(adminStats.usage?.today?.input_tokens, adminStats.usage?.today?.output_tokens) +
                              estimateDeepgramCost(adminStats.usage?.today?.audio_minutes)
                          )}{' '}
                          / {t('admin.total')}:{' '}
                          {formatUSD(
                            estimateDeepseekCost(adminStats.usage?.total?.input_tokens, adminStats.usage?.total?.output_tokens) +
                              estimateDeepgramCost(adminStats.usage?.total?.audio_minutes)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="AdminSection">
                    <div className="AdminSectionTitle">{t('admin.diskUsage')}</div>
                    <div className="AdminList">
                      <div className="AdminListRow">
                        <span>{t('admin.disk.input')}</span>
                        <span>{adminStats.disk_usage?.input?.human || t('common.na')}</span>
                      </div>
                      <div className="AdminListRow">
                        <span>{t('admin.disk.output')}</span>
                        <span>{adminStats.disk_usage?.output?.human || t('common.na')}</span>
                      </div>
                      <div className="AdminListRow">
                        <span>{t('admin.disk.temp')}</span>
                        <span>{adminStats.disk_usage?.temp?.human || t('common.na')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="AdminSection">
                    <div className="AdminSectionTitle">{t('admin.recentTasks')}</div>
                    {Array.isArray(adminStats.recent_tasks) && adminStats.recent_tasks.length > 0 ? (
                      <div className="AdminList">
                        {adminStats.recent_tasks.map((rt) => (
                          <div key={rt.id} className="AdminListRow">
                            <span className="AdminMono">{rt.id}</span>
                            <span className={`Pill Pill--${rt.status}`}>{formatStatusLabel(rt.status, t)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="SmallNote">{t('admin.noRecentTasks')}</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
